import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  ChevronLeftIcon, 
  DocumentIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  EyeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { validateFileType, validateFileSize, formatFileSize } from '../../utils/helpers';

// Validation schema for Step 3
const step3Schema = yup.object({
  useMasterAsset: yup.boolean(),
  documents: yup.object({
    photo: yup.mixed<File>().nullable().test('required', 'Photo is required', function(value) {
      const { canSkip } = (this.options.context || {}) as { canSkip?: boolean };
      if (canSkip) return true;
      return value !== null;
    }),
    masterAsset: yup.mixed<File>().nullable().test('requiredIfMaster', 'Master asset is required', function(value) {
      const { useMasterAsset, canSkip } = (this.options.context || {}) as { useMasterAsset?: boolean; canSkip?: boolean };
      if (canSkip) return true;
      if (useMasterAsset) return value !== null;
      return true;
    }),
    class10Certificate: yup.mixed<File>().nullable().test('requiredIfSeparate', '10th Certificate is required', function(value) {
      const { useMasterAsset, canSkip } = (this.options.context || {}) as { useMasterAsset?: boolean; canSkip?: boolean };
      if (canSkip) return true;
      if (!useMasterAsset) return value !== null;
      return true;
    }),
    class12Certificate: yup.mixed<File>().nullable().test('requiredIfSeparate', '12th Certificate is required', function(value) {
      const { useMasterAsset, canSkip } = (this.options.context || {}) as { useMasterAsset?: boolean; canSkip?: boolean };
      if (canSkip) return true;
      if (!useMasterAsset) return value !== null;
      return true;
    }),
    aadhaarCard: yup.mixed<File>().nullable().test('requiredIfSeparate', 'Aadhaar image is required', function(value) {
      const { useMasterAsset, canSkip } = (this.options.context || {}) as { useMasterAsset?: boolean; canSkip?: boolean };
      if (canSkip) return true;
      if (!useMasterAsset) return value !== null;
      return true;
    }),
    casteCertificate: yup.mixed<File>().nullable().test('requiredIfSCST', 'Caste certificate is required for OBC/SC/ST candidates', function(value) {
      const { caste, useMasterAsset, canSkip } = (this.options.context || {}) as { caste?: string; useMasterAsset?: boolean; canSkip?: boolean };
      if (canSkip) return true;
      if (!useMasterAsset && (caste === 'SC' || caste === 'ST' || caste === 'OBC')) {
        return value !== null;
      }
      return true;
    }),
    degreeCertificate: yup.mixed<File>().nullable().notRequired(),
    sportsCertificates: yup.array().of(yup.mixed<File>()).default([])
  })
});

interface DocumentData {
  useMasterAsset?: boolean;
  documents: {
    photo: File | null;
    masterAsset: File | null;
    class10Certificate: File | null;
    class12Certificate: File | null;
    aadhaarCard: File | null;
    casteCertificate: File | null;
    degreeCertificate: File | null;
    sportsCertificates: File[];
  };
}

interface DocumentUploadStepProps {
  data: Partial<DocumentData>;
  caste?: string;
  canSkip?: boolean;
  onNext: (data: DocumentData) => void;
  onSkipAndSubmit?: () => void;
  onBack: (data?: any) => void;
  isSubmitting?: boolean;
}

interface FileUploadProps {
  label: string;
  description: string;
  file: File | null;
  onChange: (file: File | null) => void;
  accept: string;
  maxSize: number;
  required?: boolean;
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  description,
  file,
  onChange,
  accept,
  maxSize,
  required = false,
  error
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    setUploadError('');
    
    // Validate file type
    const allowedTypes = accept.split(',').map(type => type.trim());
    if (!validateFileType(selectedFile, allowedTypes)) {
      setUploadError('Invalid file type. Please select a valid file.');
      return;
    }
    
    // Validate file size
    if (!validateFileSize(selectedFile, maxSize)) {
      setUploadError(`File size should be less than ${maxSize}MB`);
      return;
    }
    
    onChange(selectedFile);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const removeFile = () => {
    onChange(null);
    setUploadError('');
  };

  const previewFile = () => {
    if (file) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
    }
  };

  return (
    <div className="space-y-2">
      <label className="form-label">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      {file ? (
        // File selected view
        <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
              <div>
                <p className="font-medium text-green-900">{file.name}</p>
                <p className="text-sm text-green-700">{formatFileSize(file.size)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={previewFile}
                className="p-2 text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                title="Preview file"
              >
                <EyeIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={removeFile}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title="Remove file"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Upload area
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
            dragOver 
              ? 'border-primary-500 bg-primary-50' 
              : error || uploadError
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => document.getElementById(`file-${label.replace(/\s+/g, '-').toLowerCase()}`)?.click()}
        >
          <input
            id={`file-${label.replace(/\s+/g, '-').toLowerCase()}`}
            type="file"
            accept={accept}
            onChange={handleFileInputChange}
            className="hidden"
          />
          
          <CloudArrowUpIcon className={`w-12 h-12 mx-auto mb-4 ${
            error || uploadError ? 'text-red-400' : 'text-gray-400'
          }`} />
          
          <p className={`text-lg font-medium mb-2 ${
            error || uploadError ? 'text-red-900' : 'text-gray-900'
          }`}>
            Drop file here or click to upload
          </p>
          
          <p className={`text-sm mb-2 ${
            error || uploadError ? 'text-red-700' : 'text-gray-600'
          }`}>
            {description}
          </p>
          
          <p className={`text-xs ${
            error || uploadError ? 'text-red-600' : 'text-gray-500'
          }`}>
            Maximum file size: {maxSize}MB
          </p>
        </div>
      )}
      
      {(error || uploadError) && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <ExclamationTriangleIcon className="w-5 h-5" />
          <span>{error || uploadError}</span>
        </div>
      )}
    </div>
  );
};

const DocumentUploadStep: React.FC<DocumentUploadStepProps> = ({ 
  data, 
  caste,
  canSkip = true,
  onNext, 
  onSkipAndSubmit,
  onBack, 
  isSubmitting = false 
}) => {
  const [useMasterAsset, setUseMasterAsset] = useState(data.useMasterAsset || false);
  const [documents, setDocuments] = useState({
    photo: data.documents?.photo || null,
    masterAsset: data.documents?.masterAsset || null,
    class10Certificate: data.documents?.class10Certificate || null,
    class12Certificate: data.documents?.class12Certificate || null,
    aadhaarCard: data.documents?.aadhaarCard || null,
    casteCertificate: data.documents?.casteCertificate || null,
    degreeCertificate: data.documents?.degreeCertificate || null,
    sportsCertificates: data.documents?.sportsCertificates || []
  });

  const isCasteCertificateRequired = caste === 'SC' || caste === 'ST' || caste === 'OBC';

  const { 
    handleSubmit,
    formState: { errors },
    setValue,
    trigger
  } = useForm<any>({
    resolver: yupResolver(step3Schema),
    context: { caste, useMasterAsset, canSkip },
    defaultValues: { useMasterAsset, documents }
  });

  React.useEffect(() => {
    setValue('useMasterAsset', useMasterAsset);
    setValue('documents', documents);
    trigger();
  }, [useMasterAsset, documents, setValue, trigger]);

  const updateDocument = (key: keyof typeof documents, file: File | null) => {
    if (key === 'sportsCertificates') return;
    
    setDocuments(prev => ({
      ...prev,
      [key]: file
    }));
  };

  const addSportsCertificate = (file: File) => {
    setDocuments(prev => ({
      ...prev,
      sportsCertificates: [...prev.sportsCertificates, file]
    }));
  };

  const removeSportsCertificate = (index: number) => {
    setDocuments(prev => ({
      ...prev,
      sportsCertificates: prev.sportsCertificates.filter((_, i) => i !== index)
    }));
  };

  const handleBack = () => {
    onBack?.({
      useMasterAsset,
      documents
    });
  };

  const onSubmit = (formData: any) => {
    onNext({ 
      useMasterAsset,
      documents 
    });
  };

  const documentRequirements = [
    {
      title: 'Photo Requirements',
      items: [
        'Recent passport-size photograph',
        'Clear and high-quality image',
        'White or light blue background',
        'File size: Maximum 2MB'
      ]
    },
    {
      title: useMasterAsset ? 'Master Asset (Required)' : '10th Certificate (Required)',
      items: useMasterAsset ? [
        'Single PDF or ZIP containing ALL documents',
        'Includes 10th, 12th, Aadhaar, Caste, etc.',
        'File size: Maximum 20MB'
      ] : [
        'Clear scan or photo of 10th marksheet/certificate',
        'All details should be readable',
        'File size: Maximum 5MB'
      ]
    },
    {
      title: !useMasterAsset && isCasteCertificateRequired ? 'Caste Certificate (Required)' : 'Caste Certificate (Optional)',
      items: [
        'Clear scan or photo of your caste certificate',
        'Necessary for reservation verification',
        'File size: Maximum 5MB'
      ]
    }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-warning-600 rounded-full flex items-center justify-center mr-4">
            <DocumentIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Document Upload</h2>
            <p className="text-gray-600">Please upload all required documents</p>
          </div>
        </div>

        {canSkip && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-300 rounded-xl flex flex-wrap items-center justify-between gap-3 text-emerald-900">
            <div className="flex items-start space-x-3">
              <SparklesIcon className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-emerald-900">Document Upload Is Optional</h4>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Uploading documents is optional. You can upload any available files or submit directly without uploading documents.
                </p>
              </div>
            </div>
            {onSkipAndSubmit && (
              <button
                type="button"
                onClick={onSkipAndSubmit}
                disabled={isSubmitting}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors flex items-center space-x-1 disabled:opacity-50"
              >
                <CheckCircleIcon className="w-3.5 h-3.5" />
                <span>{isSubmitting ? 'Submitting...' : 'Submit Without Documents →'}</span>
              </button>
            )}
          </div>
        )}

        {/* Master Asset Toggle */}
        <div className="mb-8 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900">Upload Master Asset?</h4>
              <p className="text-sm text-gray-600">Choose this if you have all documents in a single PDF file</p>
            </div>
            <button
              type="button"
              onClick={() => setUseMasterAsset(!useMasterAsset)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                useMasterAsset ? 'bg-primary-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  useMasterAsset ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Document Upload Section */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              {/* Photo Upload */}
              <FileUpload
                label="Passport Size Photo"
                description={canSkip ? "Upload a recent passport-size photograph (Optional)" : "Upload a recent passport-size photograph (JPG, PNG)"}
                file={documents.photo}
                onChange={(file) => updateDocument('photo', file)}
                accept="image/jpeg,image/png,image/jpg"
                maxSize={2}
                required={!canSkip}
                error={errors.documents && 'photo' in errors.documents && (errors.documents as any).photo ? String((errors.documents as any).photo.message || '') : undefined}
              />

              {useMasterAsset ? (
                /* Master Asset Upload */
                <FileUpload
                  label="Master Asset (All Documents)"
                  description="Upload a single PDF containing all your certificates (10th, 12th, Aadhaar, etc.)"
                  file={documents.masterAsset}
                  onChange={(file) => updateDocument('masterAsset', file)}
                  accept="application/pdf,image/jpeg,image/png"
                  maxSize={20}
                  required={!canSkip}
                  error={errors.documents && 'masterAsset' in errors.documents && (errors.documents as any).masterAsset ? String((errors.documents as any).masterAsset.message || '') : undefined}
                />
              ) : (
                /* Separate Document Uploads */
                <>
                  <FileUpload
                    label="10th Certificate"
                    description={canSkip ? "Upload your 10th class marksheet/certificate (Optional)" : "Upload your 10th class marksheet/certificate (JPG, PNG, PDF)"}
                    file={documents.class10Certificate}
                    onChange={(file) => updateDocument('class10Certificate', file)}
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    maxSize={5}
                    required={!canSkip}
                    error={errors.documents && 'class10Certificate' in errors.documents && (errors.documents as any).class10Certificate ? String((errors.documents as any).class10Certificate.message || '') : undefined}
                  />

                  <FileUpload
                    label="12th Certificate"
                    description={canSkip ? "Upload your 12th class marksheet/certificate (Optional)" : "Upload your 12th class marksheet/certificate (JPG, PNG, PDF)"}
                    file={documents.class12Certificate}
                    onChange={(file) => updateDocument('class12Certificate', file)}
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    maxSize={5}
                    required={!canSkip}
                    error={errors.documents && 'class12Certificate' in errors.documents && (errors.documents as any).class12Certificate ? String((errors.documents as any).class12Certificate.message || '') : undefined}
                  />

                  <FileUpload
                    label="Aadhaar Card"
                    description={canSkip ? "Upload your Aadhaar card (Optional)" : "Upload your Aadhaar card (JPG, PNG, PDF)"}
                    file={documents.aadhaarCard}
                    onChange={(file) => updateDocument('aadhaarCard', file)}
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    maxSize={5}
                    required={!canSkip}
                    error={errors.documents && 'aadhaarCard' in errors.documents && (errors.documents as any).aadhaarCard ? String((errors.documents as any).aadhaarCard.message || '') : undefined}
                  />

                  {(isCasteCertificateRequired || documents.casteCertificate) && (
                    <FileUpload
                      label="Caste Certificate"
                      description={`Upload your caste certificate (${caste}) (JPG, PNG, PDF)`}
                      file={documents.casteCertificate}
                      onChange={(file) => updateDocument('casteCertificate', file)}
                      accept="image/jpeg,image/png,image/jpg,application/pdf"
                      maxSize={5}
                      required={!canSkip && isCasteCertificateRequired}
                      error={errors.documents && 'casteCertificate' in errors.documents && (errors.documents as any).casteCertificate ? String((errors.documents as any).casteCertificate.message || '') : undefined}
                    />
                  )}

                  <FileUpload
                    label="Degree Certificate"
                    description="Upload your degree certificate if applicable (JPG, PNG, PDF)"
                    file={documents.degreeCertificate}
                    onChange={(file) => updateDocument('degreeCertificate', file)}
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    maxSize={5}
                    required={false}
                    error={errors.documents && 'degreeCertificate' in errors.documents && (errors.documents as any).degreeCertificate ? String((errors.documents as any).degreeCertificate.message || '') : undefined}
                  />
                </>
              )}

              {/* Sports Certificates Upload */}
              <div className="space-y-4">
                <label className="form-label">
                  Sports Certificates <span className="text-gray-500">(Optional)</span>
                </label>
                
                {/* Add new certificate */}
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-400 hover:bg-gray-50 transition-colors"
                  onClick={() => document.getElementById('sports-certificates')?.click()}
                >
                  <input
                    id="sports-certificates"
                    type="file"
                    accept="image/jpeg,image/png,image/jpg,application/pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && validateFileSize(file, 5)) {
                        addSportsCertificate(file);
                        e.target.value = ''; // Reset input
                      }
                    }}
                    className="hidden"
                  />
                  
                  <CloudArrowUpIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600">Click to add sports certificates</p>
                  <p className="text-xs text-gray-500">JPG, PNG, PDF - Maximum 5MB each</p>
                </div>

                {/* Display uploaded certificates */}
                {documents.sportsCertificates.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">Uploaded Certificates ({documents.sportsCertificates.length})</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {documents.sportsCertificates.map((file, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                          <div className="flex items-center space-x-2 min-w-0">
                            <DocumentIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSportsCertificate(index)}
                            className="text-red-600 hover:text-red-800 transition-colors p-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn-secondary flex items-center space-x-2"
                  disabled={isSubmitting}
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                  <span>Back to Education</span>
                </button>
                
                <div className="flex flex-wrap items-center gap-3 ml-auto">
                  {canSkip && onSkipAndSubmit && (
                    <button
                      type="button"
                      onClick={onSkipAndSubmit}
                      disabled={isSubmitting}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-4 py-2.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                    >
                      Submit Without Documents
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn-primary flex items-center space-x-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Submitting Application...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-5 h-5" />
                        <span>Submit Application</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Requirements Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Requirements</h3>
              
              <div className="space-y-6">
                {documentRequirements.map((requirement, index) => (
                  <div key={index}>
                    <h4 className="font-medium text-gray-900 mb-2">{requirement.title}</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {requirement.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-2">
                          <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-blue-100 rounded-lg">
                <div className="flex items-start space-x-2">
                  <DocumentIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Important Note</p>
                    <p>All documents will be verified. Please ensure they are clear, readable, and authentic.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadStep;